import { z } from "zod";
import { InquiryEventTypeSchema, InquiryStageSchema } from "@/api/types/inquiry";
import { PackageCodeSchema } from "@/api/types/package";
import { QuoteStatusSchema } from "@/api/types/quote";
import { EventStatusSchema } from "@/api/types/event";
import { InvoiceKindSchema, InvoiceStatusSchema } from "@/api/types/invoice";
import { PaymentMethodSchema } from "@/api/types/payment";
import { ReviewPlatformSchema } from "@/api/types/review";
import { OutreachStatusSchema, OutreachTierSchema } from "@/api/types/outreach";
import { InteractionDirectionSchema, InteractionTypeSchema } from "@/api/types/interaction";
import { ContactSchema } from "@/api/types/contact";

/**
 * Contact 360 overview (Phase 10). GET /api/v2/contacts/{id}/overview merges
 * every association plus a pre-built timeline in one call. Each association
 * array is a LIGHT projection (only the fields the 360 page needs) — not the
 * full entity shape from that section's own types/*.ts. Full rows are one
 * click away via each association table's link to the section's own page.
 *
 * Every row here is deliberately tolerant (nullable/optional beyond what the
 * contract states) — B10-1 (api) builds the same contract in parallel from
 * this same spec, so minor field-presence drift shouldn't trip a schema
 * violation on either side.
 */

export const OverviewInquirySchema = z.object({
  id: z.string(),
  event_date: z.string().nullable().optional(),
  event_type: InquiryEventTypeSchema.nullable().optional(),
  stage: InquiryStageSchema,
  guest_count: z.number().nullable().optional(),
  created_at: z.string(),
});
export type OverviewInquiry = z.infer<typeof OverviewInquirySchema>;

export const OverviewQuoteSchema = z.object({
  id: z.string(),
  inquiry_id: z.string().nullable().optional(),
  package_code: PackageCodeSchema.nullable().optional(),
  total_cents: z.number(),
  status: QuoteStatusSchema,
  created_at: z.string(),
});
export type OverviewQuote = z.infer<typeof OverviewQuoteSchema>;

export const OverviewEventSchema = z.object({
  id: z.string(),
  event_date: z.string().nullable().optional(),
  status: EventStatusSchema,
  guest_count: z.number().nullable().optional(),
  venue_name: z.string().nullable().optional(),
});
export type OverviewEvent = z.infer<typeof OverviewEventSchema>;

export const OverviewInvoiceSchema = z.object({
  id: z.string(),
  event_id: z.string().nullable().optional(),
  kind: InvoiceKindSchema,
  number: z.string().nullable().optional(),
  total_cents: z.number(),
  status: InvoiceStatusSchema,
  paid_cents: z.number().nullable().optional(),
});
export type OverviewInvoice = z.infer<typeof OverviewInvoiceSchema>;

export const OverviewPaymentSchema = z.object({
  id: z.string(),
  invoice_id: z.string().nullable().optional(),
  method: PaymentMethodSchema,
  amount_cents: z.number(),
  received_at: z.string(),
});
export type OverviewPayment = z.infer<typeof OverviewPaymentSchema>;

export const OverviewReviewSchema = z.object({
  id: z.string(),
  platform: ReviewPlatformSchema,
  rating: z.number().nullable().optional(),
  url: z.string().nullable().optional(),
  received_at: z.string(),
});
export type OverviewReview = z.infer<typeof OverviewReviewSchema>;

export const OverviewOutreachSchema = z.object({
  id: z.string(),
  tier: OutreachTierSchema,
  status: OutreachStatusSchema,
  verified: z.boolean(),
  last_touch_at: z.string().nullable().optional(),
  next_touch_at: z.string().nullable().optional(),
});
export type OverviewOutreach = z.infer<typeof OverviewOutreachSchema>;

export const OverviewInteractionSchema = z.object({
  id: z.string(),
  type: InteractionTypeSchema,
  direction: InteractionDirectionSchema.nullable().optional(),
  subject: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  occurred_at: z.string(),
});
export type OverviewInteraction = z.infer<typeof OverviewInteractionSchema>;

export const OverviewActivitySchema = z.object({
  id: z.string(),
  method: z.string(),
  path: z.string(),
  entity: z.string().nullable().optional(),
  entity_id: z.string().nullable().optional(),
  created_at: z.string(),
});
export type OverviewActivity = z.infer<typeof OverviewActivitySchema>;

// The merged, newest-first history row. `kind` is intentionally z.string()
// (not a strict enum) — the timeline mixes logged-interaction kinds
// (call/text/email/note/meeting) with lifecycle kinds (inquiry/quote/event/
// invoice/payment/review/...) and new lifecycle kinds are a one-line backend
// add; an unrecognized kind still renders with a fallback icon (see
// timelineIcon() in ContactDetailPage.tsx) rather than failing validation.
export const TimelineRowSchema = z.object({
  kind: z.string(),
  at: z.string(),
  label: z.string(),
  ref_id: z.string().nullable().optional(),
});
export type TimelineRow = z.infer<typeof TimelineRowSchema>;

export const ContactOverviewCountsSchema = z.object({
  inquiries: z.number(),
  quotes: z.number(),
  events: z.number(),
  invoices: z.number(),
  reviews: z.number(),
  outreach: z.number(),
  interactions: z.number(),
});
export type ContactOverviewCounts = z.infer<typeof ContactOverviewCountsSchema>;

export const ContactOverviewSchema = z.object({
  contact: ContactSchema,
  inquiries: z.array(OverviewInquirySchema),
  quotes: z.array(OverviewQuoteSchema),
  events: z.array(OverviewEventSchema),
  invoices: z.array(OverviewInvoiceSchema),
  payments: z.array(OverviewPaymentSchema),
  reviews: z.array(OverviewReviewSchema),
  outreach: z.array(OverviewOutreachSchema),
  interactions: z.array(OverviewInteractionSchema),
  activities: z.array(OverviewActivitySchema),
  timeline: z.array(TimelineRowSchema),
  counts: ContactOverviewCountsSchema,
});
export type ContactOverview = z.infer<typeof ContactOverviewSchema>;
